import { model, Model, prop } from "mobx-keystone";
import { computed } from "mobx";
import { DEPOSIT_CLOSED_PHASES, PHASE_COLORS, PHASE_LABELS } from "../helpers/constants";
import { SwapOrderPayload } from "../helpers/types";

@model("lightningswap/SwapOrder")
export class SwapOrder extends Model({
  id:                    prop<number>(),
  publicToken:           prop<string>(),
  providerOrderId:       prop<string>(),
  phase:                 prop<string>(),
  apiStatus:             prop<string>(),
  fromCode:              prop<string>(),
  toCode:                prop<string>(),
  amountSats:            prop<number>(),
  bolt11:                prop<string | undefined>(undefined),
  depositAddress:        prop<string | null>(null),
  depositMemo:           prop<string | null>(null),
  depositAmount:         prop<string>(),
  depositTxId:           prop<string | null>(null),
  depositTxAmount:       prop<string | null>(null),
  depositConfirmations:  prop<number | null>(null),
  payoutTxId:            prop<string | null>(null),
  refundTxId:            prop<string | null>(null),
  refundReason:          prop<string | null>(null),
  refundAmount:          prop<string | null>(null),
  emergencyStatus:       prop<string[]>(() => []),
  emergencyChoice:       prop<string>("NONE"),
  emergencyRepeat:       prop<boolean>(false),
  attention:             prop<boolean>(false),
  attentionReason:       prop<string | null>(null),
  overpayKept:           prop<boolean>(false),
  refundClaimUntil:      prop<number | null>(null),
  refundLapsed:          prop<boolean>(false),
  expiresAt:             prop<number>(),
  timeLeft:              prop<number>(),
  reqConfirmations:      prop<number>(1),
  createdAt:             prop<string>(),
  updatedAt:             prop<string>()
}) {
  static fromPayload(data: SwapOrderPayload): SwapOrder {
    return new SwapOrder({
      id:                   data.id,
      publicToken:          data.public_token,
      providerOrderId:      data.provider_order_id,
      phase:                data.phase,
      apiStatus:            data.api_status,
      fromCode:             data.from_code,
      toCode:               data.to_code,
      amountSats:           data.amount_sats,
      bolt11:               data.bolt11,
      depositAddress:       data.deposit_address,
      depositMemo:          data.deposit_memo,
      depositAmount:        data.deposit_amount,
      depositTxId:          data.deposit_tx_id,
      depositTxAmount:      data.deposit_tx_amount,
      depositConfirmations: data.deposit_confirmations,
      payoutTxId:           data.payout_tx_id,
      refundTxId:           data.refund_tx_id,
      refundReason:         data.refund_reason,
      refundAmount:         data.refund_amount,
      emergencyStatus:      data.emergency_status ?? [],
      emergencyChoice:      data.emergency_choice ?? "NONE",
      emergencyRepeat:      data.emergency_repeat ?? false,
      attention:            data.attention ?? false,
      attentionReason:      data.attention_reason,
      overpayKept:          data.overpay_kept ?? false,
      refundClaimUntil:     data.refund_claim_until ?? null,
      refundLapsed:         data.refund_lapsed ?? false,
      expiresAt:            data.expires_at,
      timeLeft:             data.time_left,
      reqConfirmations:     data.req_confirmations,
      createdAt:            data.created_at,
      updatedAt:            data.updated_at
    });
  }

  @computed
  get phaseLabel(): string {
    return PHASE_LABELS[this.phase] ?? this.phase;
  }

  @computed
  get phaseColor(): string {
    return PHASE_COLORS[this.phase] ?? "muted";
  }

  @computed
  get needsRefund(): boolean {
    // Show the form whenever a self-serve refund can be (re)submitted.
    // Failed broadcasts roll back to refund_required; refund_pending without
    // a tx id is a brief in-flight state and keeps the form hidden. Gone for
    // good once the refund lapsed unclaimed (7 days, UnclaimedRefunds).
    return this.phase === "refund_required" && !this.overpayKept && !this.refundLapsed;
  }

  @computed
  get isOpen(): boolean {
    return !["completed", "refunded", "failed", "expired"].includes(this.phase);
  }

  // Narrower than isOpen: refund_required/attention are still "open" but must
  // not advertise a deposit address, or the payer sends a second time into an
  // order we are already returning.
  @computed
  get acceptsDeposit(): boolean {
    return !DEPOSIT_CLOSED_PHASES.includes(this.phase);
  }
}
